import { useContext, useEffect, useState } from 'react';
import ivclass from '@invideoio/web-shared/Modules/IVClass';

import AppLayout from '@layouts/AppLayout';

import { BlogCardProps } from 'src/types';
import { StateContext } from '@store/store';
import { fetchAllTrendingList, fetchTotalTrendingPostsCount } from '@lib/API';

import HeaderWithCTA from '@components/header-with-cta';
import BlogPostCard from '@components/blog-posts/blog-post-card';
import ShowMoreButton from '@components/showmore-button';
import TrendingPostPageHeader from '@components/trending-post-page-header';
import BlogSEO from '@components/SEO/BlogSEO';
import Spinner from '@components/common/Spinner';
import useBlogAppLoad from '@hooks/useBlogAppLoad';
import useTranslation from 'next-translate/useTranslation';

type API_STATE = 'IDLE' | 'PENDING' | 'SUCESS' | 'FAILED';

const TrendingPostsPage = () => {
  const globalState = useContext(StateContext);
  const { platform, browser } = globalState;

  const { lang } = useTranslation();

  const [trendingPosts, setTrendingPosts] = useState<BlogCardProps[]>([]);
  const [trendingPostsCount, setTrendingPostsCount] = useState<number>(0);

  const [status, setStatus] = useState<API_STATE>('IDLE');

  useBlogAppLoad({ platform });

  async function loadMorePosts() {
    setStatus('PENDING');

    const posts = await fetchAllTrendingList({
      start: trendingPosts.length,
      limit: 9,
      language: lang,
    });
    setTrendingPosts(trendingPosts => [...trendingPosts, ...posts]);
    setStatus('SUCESS');
  }

  async function getTotalTrendingPostsCount() {
    const count = await fetchTotalTrendingPostsCount({ language: lang });
    setTrendingPostsCount(count);
  }

  useEffect(() => {
    loadMorePosts();
    getTotalTrendingPostsCount();
  }, []);

  return (
    <AppLayout platform={platform}>
      <BlogSEO
        seo={{
          Title: 'Top trending posts | InVideo Blog',
          CanonicalURL: 'https://invideo.io/blog/trending/',
          id: 0,
          Hreflang: [],
          Keywords: '',
          Meta: [],
          RobotFollow: null,
          RobotIndex: null,
          Description:
            'InVideo is an online video creator for content and marketing videos. Read our blogs tolearn everything about creating professional quality videos.',
        }}
        url="/blog/trending/"
      />
      <HeaderWithCTA />
      <TrendingPostPageHeader title={'Top trending posts 🔥'} />
      <section
        className={ivclass(
          'iv-bg-black-4',
          'iv-pt-[65px] md:iv-pt-[35px] sm:iv-pt-[28px]',
          'iv-pb-100 md:iv-pb-80 sm:iv-pb-50',
        )}
      >
        <div className={ivclass('iv-container iv-mx-auto')}>
          <div className={ivclass('iv-grid iv-grid-cols-12')}>
            <div
              className={ivclass(
                'iv-col-span-12',
                'iv-mx-100 lg:iv-mx-[80px] md:iv-mx-25 sm:iv-mx-20',
              )}
            >
              <div
                className={ivclass(
                  'iv-grid iv-grid-cols-3 md:iv-grid-cols-2 sm:iv-grid-cols-1',
                  'iv-col-span-12',
                  'iv-gap-x-50 lg:iv-gap-x-40 md:iv-gap-x-35 ',
                  'iv-gap-y-40 md:iv-gap-y-25 sm:iv-gap-y-15',
                )}
              >
                {trendingPosts?.map(post => (
                  <div className="iv-mx-auto" key={post.id}>
                    <BlogPostCard
                      {...post}
                      devicePlatform={platform}
                      browser={browser}
                      backgroundColorClass={'iv-bg-grey-0'}
                    />
                  </div>
                ))}
                {status === 'PENDING' && (
                  <div
                    className={ivclass(
                      'iv-col-span-3 md:iv-col-span-2 sm:iv-col-span-1',
                      'iv-px-20 lg:iv-px-[0] md:iv-px-20',
                      'iv-my-40 iv-mx-auto',
                      'iv-h-32',
                    )}
                  >
                    <div className="iv-mx-auto iv-text-center">
                      <Spinner
                        color="#3399ff"
                        size={16}
                        style={{ margin: '0 20px' }}
                      />
                      <span
                        className={ivclass(
                          'iv-block',
                          'iv-my-15',
                          'iv-text-body-3xl iv-font-medium iv-text-grey-60',
                          'iv-whitespace-nowrap',
                        )}
                      >
                        Fetching Articles
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
            {Array.isArray(trendingPosts) &&
              trendingPosts.length > 0 &&
              trendingPostsCount !== trendingPosts.length && (
                <div
                  className={ivclass(
                    'iv-col-span-12',
                    'iv-mt-100 md:iv-mt-50 sm:iv-mt-30',
                    'iv-flex iv-justify-center',
                  )}
                >
                  <ShowMoreButton
                    isPaginationEnded={
                      trendingPostsCount == trendingPosts.length
                    }
                    showMoreAction={loadMorePosts}
                  />
                </div>
              )}
          </div>
        </div>
      </section>
    </AppLayout>
  );
};

export default TrendingPostsPage;
